import { useState, useEffect, useRef } from "react";
import { db, functions } from "../firebaseConfig";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";

import { getDocs, collection, query, where } from "firebase/firestore";

import Header from "../components/Header";
import { useDesignVersion } from "../DesignVersionContext";
import ExcelV2 from "./ExcelV2";
import common from "../style/screens/Common.module.css";
import style from "../style/screens/Excel.module.css";
import { ListButton } from "../components/Buttons";
import { KioskType, useKiosk } from "../KioskContext";

type Receipt = {
    id: string;
    inputAmt: number;
    kiosk: string;
    owner: string;
    timestamp: number;
};

const Excel = () => {
    const { version } = useDesignVersion();
    const partnerId = sessionStorage.getItem("partnerId"); // partnerId 추가
    const { kiosks } = useKiosk();
    const [selected, setSelected] = useState<string[]>([]);

    const [startDate, setStartDate] = useState(new Date());
    const [endDate, setEndDate] = useState(new Date());

    const toggleAll = () => {
        if (selected.length == kiosks.length) {
            setSelected([]);
        } else {
            setSelected(kiosks.map((k) => k.docId));
        }
    };

    const [loading, setLoading] = useState<boolean>(false);
    const [search, setSearch] = useState<string>("");

    if (version === "v2") {
        return <ExcelV2 />;
    }

    const createExcel = async () => {
        if (!partnerId) {
            alert("partnerId가 없습니다. 다시 로그인해주세요.");
            return;
        }

        if (startDate.getTime() > endDate.getTime()) {
            alert("시작일을 종료일 이전으로 선택해주세요");
            return;
        }

        if (!selected.length) {
            alert("키오스크를 선택해주세요");
            return;
        }

        setLoading(true);

        // firebase "in" 최대가 30개
        const chunkSize = 30;
        let allReceipts: any[] = [];

        for (let i = 0; i < selected.length; i += chunkSize) {
            const chunk = selected.slice(i, i + chunkSize);

            const snapshot = await getDocs(
                query(
                    collection(db, "receipt"),
                    where("partnerId", "==", partnerId), // partnerId 필터링 추가
                    where("timestamp", ">=", startDate.getTime()),
                    where("timestamp", "<=", endDate.getTime()),
                    where("kiosk", "in", chunk)
                )
            );

            const receipts = snapshot.docs.map((doc) => {
                const data = doc.data();
                const kioskInfo = kiosks.find((k) => k.docId === data.kiosk);
                return {
                    id: doc.id,
                    inputAmt: data.inputAmt,
                    inputType: data.inputType,
                    kiosk: kioskInfo ? kioskInfo.name : data.kiosk,
                    owner: data.owner,
                    timestamp: data.timestamp,
                };
            });

            allReceipts = allReceipts.concat(receipts);
        }

        setLoading(false);

        downloadReceiptsAsExcel(allReceipts);
    };

    const downloadReceiptsAsExcel = (receipts: any[]) => {
        if (receipts.length === 0) {
            alert("데이터가 존재하지 않습니다!");
            return;
        }

        receipts.sort((a, b) => {
            if (a.kiosk < b.kiosk) return -1;
            if (a.kiosk > b.kiosk) return 1;
            return a.timestamp - b.timestamp;
        });

        const headerMap: Record<string, string> = {
            id: "영수증 ID",
            inputAmt: "투입 수량",
            kiosk: "키오스크 ID",
            owner: "유저 ID",
            timestamp: "시각",
            carbonReduction: "탄소 절감량 (gCO₂)",
        };

        const orderedKeys: string[] = ["kiosk", "timestamp", "inputAmt", "carbonReduction", "owner", "id"];

        const data = receipts.map((r) => {
            const row: any = {};
            orderedKeys.forEach((key) => {
                if (key === "timestamp") {
                    row[headerMap[key]] = new Date(r.timestamp).toLocaleString("ko-KR");
                } else if (key === "carbonReduction") {
                    // 탄소 절감량 계산: inputAmt * 66g
                    const reduction = r.inputAmt * 66;
                    row[headerMap[key]] = `${reduction.toLocaleString()} g`;
                } else {
                    row[headerMap[key]] = r[key];
                }
            });
            return row;
        });

        const worksheet = XLSX.utils.json_to_sheet(data);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, "Receipts");

        const excelBuffer = XLSX.write(workbook, {
            bookType: "xlsx",
            type: "array",
        });

        const blob = new Blob([excelBuffer], {
            type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        });

        saveAs(blob, `kiosk_report__${formatDate(startDate)}_${formatDate(endDate)}.xlsx`);
    };

    return (
        <>
            <Header />
            <div className={common["container"]}>
                <div className={common["wrapper"]}>
                    <h4 className={common["text--title"]}>엑셀 출력</h4>
                    <p className={common["text--copy"]}>
                        키오스크와 날짜를 선택하여, 페트병 데이터를 다운로드할 수 있습니다
                    </p>

                    <div className={style["wrapper--date-time"]}>
                        <DatePicker label="시작일" value={startDate} onChange={setStartDate} />
                        <DatePicker label="종료일" value={endDate} onChange={setEndDate} />
                    </div>

                    <div className={style["wrapper--download"]}>
                        <ListButton text="엑셀 만들기" loading={loading} onClick={createExcel} />
                    </div>

                    {kiosks && (
                        <>
                            <input
                                id="search-kiosk"
                                className={common["input--kiosk-filter"]}
                                type="text"
                                placeholder="아이디, 이름 또는 주소로 검색"
                                onChange={(e) => {
                                    setSearch(e.target.value);
                                }}
                            />
                            <div className={style["wrapper--kiosk-list"]}>
                                <div className={style["unit--kiosk-list"]} onClick={toggleAll}>
                                    <div
                                        className={[
                                            style["unit--kiosk-list__checkbox"],
                                            selected.length == kiosks.length &&
                                                style["unit--kiosk-list__checkbox--selected"],
                                        ]
                                            .filter(Boolean)
                                            .join(" ")}
                                    >
                                        {selected.length == kiosks.length && (
                                            <span className={style["unit--kiosk-list__checkmark"]}></span>
                                        )}
                                    </div>
                                    전체선택
                                </div>
                                {kiosks
                                    .filter((kiosk) => {
                                        return (
                                            (kiosk.status == "ACTIVE" && search === "") ||
                                            (search &&
                                                (kiosk.id.toLowerCase().includes(search.toLowerCase()) ||
                                                    kiosk.name!.toLowerCase().includes(search.toLowerCase()) ||
                                                    kiosk.address!.toLowerCase().includes(search.toLowerCase())))
                                        );
                                    })
                                    .map((k) => (
                                        <KioskList
                                            key={k.docId}
                                            name={k.name!}
                                            selected={selected.includes(k.docId)}
                                            onClick={() =>
                                                setSelected((prev) =>
                                                    prev.includes(k.docId)
                                                        ? prev.filter((i) => i !== k.docId)
                                                        : [...prev, k.docId]
                                                )
                                            }
                                        />
                                    ))}
                            </div>
                        </>
                    )}
                </div>
            </div>
        </>
    );
};

const formatDate = (date: Date) =>
    `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;

const KioskList = ({ name, selected, onClick }: { name: string; selected: boolean; onClick: () => void }) => {
    return (
        <div
            className={[style["unit--kiosk-list"], selected && style["unit--kiosk-list--selected"]]
                .filter(Boolean)
                .join(" ")}
            onClick={onClick}
        >
            <div
                className={[
                    style["unit--kiosk-list__checkbox"],
                    selected && style["unit--kiosk-list__checkbox--selected"],
                ]
                    .filter(Boolean)
                    .join(" ")}
            >
                {selected && <span className={style["unit--kiosk-list__checkmark"]}></span>}
            </div>
            <p className={style["text--kiosk-list"]}>{name}</p>
        </div>
    );
};

const DatePicker = ({ label, value, onChange }: { label: string; value: Date; onChange: (date: Date) => void }) => {
    const calendar = useRef<HTMLDivElement>(null);
    const [showCalendar, setShowCalendar] = useState(false);
    const [viewMonth, setViewMonth] = useState(value.getMonth());
    const [viewYear, setViewYear] = useState(value.getFullYear());

    // 월/년 보정
    useEffect(() => {
        if (viewMonth < 0) {
            setViewYear((prev) => prev - 1);
            setViewMonth(11);
        } else if (viewMonth > 11) {
            setViewYear((prev) => prev + 1);
            setViewMonth(0);
        }
    }, [viewMonth]);

    const daysInMonth = new Date(viewYear, viewMonth + 1, 0).getDate();
    const firstDay = new Date(viewYear, viewMonth, 1).getDay();

    const handleSelect = (day: number) => {
        const selectedDate = new Date(viewYear, viewMonth, day);
        onChange(selectedDate);
        setShowCalendar(false);
    };

    const isSameDate = (d1: Date, d2: Date) =>
        d1.getFullYear() === d2.getFullYear() && d1.getMonth() === d2.getMonth() && d1.getDate() === d2.getDate();

    useEffect(() => {
        const handleClickOutside = (e: MouseEvent) => {
            if (!calendar.current?.contains(e.target as Node)) {
                if (showCalendar) {
                    setShowCalendar(false);
                }
            }
        };

        if (showCalendar) {
            document.body.addEventListener("click", handleClickOutside);
        }

        return () => {
            document.body.removeEventListener("click", handleClickOutside);
        };
    }, [showCalendar]);

    return (
        <div className={style["date-picker"]} ref={calendar}>
            <label className={style["date-label"]}>{label}</label>
            <div className={style["date-selected"]} onClick={() => setShowCalendar(!showCalendar)}>
                {formatDate(value)}
            </div>

            {showCalendar && (
                <div className={style["calendar"]}>
                    <div className={style["calendar-header"]}>
                        <button className={style["calendar-button"]} onClick={() => setViewMonth(viewMonth - 1)}>
                            ◀
                        </button>
                        <span>
                            {viewYear}년 {viewMonth + 1}월
                        </span>
                        <button className={style["calendar-button"]} onClick={() => setViewMonth(viewMonth + 1)}>
                            ▶
                        </button>
                    </div>
                    <div className={style["calendar-grid"]}>
                        {Array.from({ length: firstDay }).map((_, i) => (
                            <div key={"empty-" + i} />
                        ))}
                        {Array.from({ length: daysInMonth }).map((_, i) => {
                            const day = i + 1;
                            const current = new Date(viewYear, viewMonth, day);
                            const isSelected = isSameDate(current, value);
                            return (
                                <div
                                    key={day}
                                    className={`${style["calendar-day"]} ${
                                        isSelected ? style["calendar-day--selected"] : ""
                                    }`}
                                    onClick={() => handleSelect(day)}
                                >
                                    {day}
                                </div>
                            );
                        })}
                    </div>
                </div>
            )}
        </div>
    );
};

export default Excel;
